import { useCallback, useEffect, useRef, useState } from 'react';

const ZOOM_MIN = 50;
const ZOOM_MAX = 200;
const ZOOM_STEP = 25;
const BASE_MAX_REM = 22;
const BASE_MAX_VH = 40;
const SMALL_IMG_MAX = 180;
/** Формулы из Word Полякова: широкая низкая gif, зум-панель для них лишняя. */
const STRIP_MAX_HEIGHT = 48;
const STRIP_MIN_ASPECT = 4;
const STRIP_SCALE = 2;

function layoutForSize(width, height) {
  if (!(width > 0 && height > 0)) return 'pending';
  const isStrip = height <= STRIP_MAX_HEIGHT && (
    height <= 32 || width / height >= STRIP_MIN_ASPECT
  );
  if (isStrip) return 'strip';
  if (width <= SMALL_IMG_MAX && height <= SMALL_IMG_MAX) return 'compact';
  return 'figure';
}

/**
 * Картинка из Markdown-условия: компактный стартовый размер и масштаб 50–200%.
 * Низкие формулы — без панели зума, чуть крупнее натурального размера.
 */
export default function ResizableMarkdownImage(props) {
  const {
    node: _node,
    src,
    alt,
    title,
    className,
    style: styleProp,
    onLoad: onLoadProp,
    ...rest
  } = props;
  const [zoomPercent, setZoomPercent] = useState(100);
  const [layout, setLayout] = useState('pending');
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const imgRef = useRef(null);

  const applyNaturalSize = useCallback(
    (naturalWidth, naturalHeight) => {
      setNatural({ width: naturalWidth, height: naturalHeight });
      setLayout(layoutForSize(naturalWidth, naturalHeight));
    },
    [],
  );

  const onLoad = useCallback(
    (e) => {
      onLoadProp?.(e);
      applyNaturalSize(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight);
    },
    [onLoadProp, applyNaturalSize],
  );

  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      applyNaturalSize(el.naturalWidth, el.naturalHeight);
    }
  }, [src, applyNaturalSize]);

  const factor = zoomPercent / 100;
  const maxWidth = `min(100%, ${BASE_MAX_REM * factor}rem)`;
  const maxHeight = `${BASE_MAX_VH * factor}vh`;

  const dec = () =>
    setZoomPercent((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  const inc = () =>
    setZoomPercent((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  const reset = () => setZoomPercent(100);

  if (!src) {
    return null;
  }

  const baseStyle =
    styleProp && typeof styleProp === 'object' ? styleProp : {};

  const stripWidth = natural.width > 0 ? `${natural.width * STRIP_SCALE}px` : undefined;

  const img = (
    <img
      {...rest}
      ref={imgRef}
      src={src}
      alt={alt ?? ''}
      title={title}
      className={['markdown-statement-img__img', className].filter(Boolean).join(' ')}
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
      style={
        layout === 'strip' || layout === 'pending'
          ? {
              ...baseStyle,
              width: stripWidth,
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              margin: '0.35rem auto',
            }
          : layout === 'compact'
            ? {
                ...baseStyle,
                maxWidth: 'min(100%, 11rem)',
                maxHeight: '6rem',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'inline-block',
                verticalAlign: 'middle',
              }
            : {
                ...baseStyle,
                maxWidth,
                maxHeight,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }
      }
    />
  );

  if (layout === 'strip' || layout === 'pending') {
    return (
      <span className="markdown-statement-img markdown-statement-img--strip">
        {img}
      </span>
    );
  }

  if (layout === 'compact') {
    return (
      <span className="markdown-statement-img markdown-statement-img--inline-small">
        {img}
      </span>
    );
  }

  return (
    <figure className="markdown-statement-img">
      <div className="markdown-statement-img__scroll">
        {img}
      </div>
      <figcaption className="markdown-statement-img__toolbar">
        <button
          type="button"
          className="btn btn-outline btn-sm markdown-statement-img__btn"
          onClick={dec}
          disabled={zoomPercent <= ZOOM_MIN}
          aria-label="Уменьшить рисунок"
        >
          −
        </button>
        <span className="markdown-statement-img__zoom-label" aria-live="polite">
          {zoomPercent}%
        </span>
        <button
          type="button"
          className="btn btn-outline btn-sm markdown-statement-img__btn"
          onClick={inc}
          disabled={zoomPercent >= ZOOM_MAX}
          aria-label="Увеличить рисунок"
        >
          +
        </button>
        <button
          type="button"
          className="btn btn-outline btn-sm markdown-statement-img__btn markdown-statement-img__btn-reset"
          onClick={reset}
          aria-label="Сбросить масштаб рисунка"
        >
          Исходный
        </button>
      </figcaption>
    </figure>
  );
}
